import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, MapPin, CalendarCheck } from "lucide-react";
import { CLINIC, NAV_LINKS } from "../lib/constants";
import { EASE } from "./motion";

export const Logo = ({ light = false }) => (
  <Link to="/" className="flex items-center" data-testid="logo-link" aria-label="iNetra home">
    {light ? (
      <span className="inline-flex items-center rounded-xl bg-white px-4 py-2.5">
        <img
          src="/images/inetra-logo.png"
          alt="iNetra Laser Eye & Advanced Retina Centre — Expert Care by AIIMS (New Delhi) Alumni"
          className="h-14 w-auto"
        />
      </span>
    ) : (
      <img
        src="/images/inetra-logo.png"
        alt="iNetra Laser Eye & Advanced Retina Centre — Expert Care by AIIMS (New Delhi) Alumni"
        className="h-16 sm:h-[4.5rem] w-auto"
      />
    )}
  </Link>
);

const TopBar = () => (
  <div className="bg-ink text-white text-xs" data-testid="top-info-bar">
    <div className="container-x flex items-center justify-between gap-4 py-2">
      <p className="flex items-center gap-2 font-medium text-blue-100/90 truncate">
        <MapPin className="w-3.5 h-3.5 text-aqua-bright shrink-0" aria-hidden="true" />
        <span className="truncate">Advanced Retina & Cataract Care | Janak Puri, New Delhi</span>
      </p>
      <div className="flex items-center gap-5 shrink-0">
        <a href={CLINIC.phoneLink} className="hidden sm:flex items-center gap-1.5 font-semibold hover:text-aqua-bright transition-colors" data-testid="topbar-call-link">
          <Phone className="w-3.5 h-3.5" aria-hidden="true" /> {CLINIC.phoneDisplay}
        </a>
        <Link to="/contact#book" className="flex items-center gap-1.5 font-bold text-aqua-bright hover:text-white transition-colors" data-testid="topbar-book-link">
          <CalendarCheck className="w-3.5 h-3.5" aria-hidden="true" /> Book Appointment
        </Link>
      </div>
    </div>
  </div>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <TopBar />
      <header
        className={`sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b transition-shadow duration-300 ${
          scrolled ? "border-slate-200 shadow-[0_8px_30px_-12px_rgba(10,43,92,0.15)]" : "border-slate-100"
        }`}
        data-testid="main-header"
      >
        <div className="container-x flex items-center justify-between gap-6 py-3.5">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-full text-[13px] font-bold tracking-wide transition-colors duration-200 ${
                    isActive ? "text-ink bg-brand-mist" : "text-slate-500 hover:text-ink hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <a href={CLINIC.phoneLink} className="btn-outline !px-5 !py-2.5 !text-[13px]" data-testid="nav-call-button">
              <Phone className="w-4 h-4" aria-hidden="true" /> Call Now
            </a>
            <Link to="/contact#book" className="btn-primary !px-5 !py-2.5 !text-[13px]" data-testid="nav-book-button">
              Book Appointment
            </Link>
          </div>
          <button
            className="lg:hidden p-2.5 rounded-xl border border-slate-200 text-ink"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="mobile-menu-button"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="lg:hidden overflow-hidden border-t border-slate-100 bg-white"
              aria-label="Mobile"
              data-testid="mobile-menu"
            >
              <div className="container-x py-4 flex flex-col gap-1">
                {NAV_LINKS.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.04 * i, duration: 0.3, ease: EASE }}
                  >
                    <NavLink
                      to={l.to}
                      end={l.to === "/"}
                      data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `block px-4 py-3 rounded-xl text-sm font-bold ${
                          isActive ? "text-ink bg-brand-mist" : "text-slate-600"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};
